import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { usePortfolio } from '@/lib/portfolio';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { AlertTriangle, TrendingUp, TrendingDown, Brain, Shield, Clock, Zap, ChevronRight, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const fmt = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);
const tip = { background: '#fff', border: '1px solid #E2DDD3', borderRadius: 8, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', padding: '8px 12px' };

function mp(p, r, y) { if (r === 0) return y > 0 ? p / (y * 12) : 0; const rm = r / 12, n = y * 12; return p * (rm * Math.pow(1 + rm, n)) / (Math.pow(1 + rm, n) - 1); }

function MiniSparkline({ data, color = '#1F3A34', width = 80, height = 28 }) {
  if (!data?.length) return null;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (<svg width={width} height={height} className="inline-block"><polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>);
}

function StatusDot({ value, good, warn }) {
  const c = value >= good ? '#2E6F57' : value >= warn ? '#A67C52' : '#8A3C3C';
  return <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />;
}

export default function CashflowEngine() {
  const { assets: ctxAssets, loading: pfLoading, activeId, portfolios } = usePortfolio();
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState('base');
  const [sortBy, setSortBy] = useState('dscr');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    (async () => {
      try {
        const { data: pfs } = await api.get('/portfolios');
        if (pfs.length > 0) {
          const [cfRes, pfRes] = await Promise.all([
            api.get(`/cashflow/${pfs[0].portfolio_id}`),
            api.get(`/portfolios/${pfs[0].portfolio_id}`)
          ]);
          setData(cfRes.data);
          setAssets(pfRes.data.assets || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Compute enriched asset metrics
  const enrichedAssets = useMemo(() => {
    return assets.map(a => {
      const m = a.metrics || {};
      const la = m.loan_amount || 0, ir = m.interest_rate || 0, noi = m.noi || 0;
      const stressRate = ir + 1;
      const stressAds = mp(la, stressRate / 100, m.loan_term || 30) * 12;
      const stressDscr = stressAds > 0 ? noi / stressAds : 0;
      const efficiency = Math.min(100, Math.round((m.net_yield || 0) * 8 + (m.dscr >= 1.2 ? 20 : 0) + (m.irr > 12 ? 20 : 0) + (m.cash_on_cash > 4 ? 15 : 0)));
      const refinancePotential = ir > 3.5 && la > 0 ? Math.round(la * (ir - 3.0) / 100) : 0;
      const riskFlags = [];
      if ((m.dscr || 0) < 1.2) riskFlags.push('Low DSCR');
      if (ir > 4) riskFlags.push('High Rate');
      if ((m.vacancy_rate || 0) > 3) riskFlags.push('Vacancy');
      return { ...a, stressDscr: Math.round(stressDscr * 100) / 100, efficiency, refinancePotential, riskFlags };
    });
  }, [assets]);

  // Sort
  const sortedAssets = useMemo(() => {
    const list = [...enrichedAssets];
    list.sort((a, b) => {
      const m1 = a.metrics || {}, m2 = b.metrics || {};
      let va, vb;
      if (sortBy === 'dscr') { va = m1.dscr || 0; vb = m2.dscr || 0; }
      else if (sortBy === 'irr') { va = m1.irr || 0; vb = m2.irr || 0; }
      else if (sortBy === 'risk') { va = a.riskFlags.length; vb = b.riskFlags.length; }
      else if (sortBy === 'efficiency') { va = a.efficiency; vb = b.efficiency; }
      else if (sortBy === 'cashflow') { va = m1.net_cashflow || 0; vb = m2.net_cashflow || 0; }
      else { va = a.name; vb = b.name; }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [enrichedAssets, sortBy, sortDir]);

  // Forecast scenarios
  const forecast = useMemo(() => {
    if (!data) return [];
    const base = data.forecast || [];
    return base.map((m, i) => {
      const baseCf = m.net_cashflow || 0;
      const rentUp = baseCf * 1.01;
      const vacUp = baseCf * 0.9;
      const combined = baseCf * 0.9 * 0.99;
      return {
        month: m.month, base: baseCf,
        cumBase: baseCf * (i + 1),
        cumRent: rentUp * (i + 1),
        cumVac: vacUp * (i + 1),
        cumStress: combined * (i + 1),
      };
    });
  }, [data]);

  // AI Insights generation
  const insights = useMemo(() => {
    if (!enrichedAssets.length || !data) return [];
    const s = data.summary || {};
    const ins = [];
    const lowDscr = enrichedAssets.filter(a => (a.metrics?.dscr || 0) < 1.2);
    if (lowDscr.length) ins.push({ type: 'risk', icon: AlertTriangle, text: `${lowDscr.map(a => a.name).join(', ')} ${lowDscr.length === 1 ? 'has' : 'have'} DSCR below 1.20 covenant threshold.` });

    const totalNoi = enrichedAssets.reduce((s, a) => s + (a.metrics?.noi || 0), 0);
    const totalDebt = enrichedAssets.reduce((s, a) => s + (a.metrics?.loan_amount || 0), 0);
    const avgRate = enrichedAssets.reduce((s, a) => s + (a.metrics?.interest_rate || 0), 0) / Math.max(enrichedAssets.length, 1);
    const stressAds = enrichedAssets.reduce((s, a) => s + mp(a.metrics?.loan_amount || 0, (a.metrics?.interest_rate + 0.75) / 100, a.metrics?.loan_term || 30) * 12, 0);
    const stressDscr = stressAds > 0 ? totalNoi / stressAds : 0;
    ins.push({ type: 'stress', icon: Shield, text: `At rate +0.75%, portfolio DSCR drops to ${stressDscr.toFixed(2)}. ${stressDscr < 1.25 ? 'Covenant breach risk.' : 'Within safe range.'}` });

    const refiCandidates = enrichedAssets.filter(a => a.refinancePotential > 0).sort((a, b) => b.refinancePotential - a.refinancePotential);
    if (refiCandidates.length) ins.push({ type: 'opportunity', icon: TrendingUp, text: `Refinancing ${refiCandidates[0].name} at current market rate saves ${fmt(refiCandidates[0].refinancePotential)}/yr in debt service.` });

    const totalIdx = enrichedAssets.reduce((s, a) => s + (a.annual_rent || 0) * 0.03, 0);
    ins.push({ type: 'opportunity', icon: Zap, text: `Pending rent indexation adds ${fmt(totalIdx)}/yr to portfolio income in 2026.` });

    const worstEff = enrichedAssets.reduce((w, a) => a.efficiency < w.efficiency ? a : w, enrichedAssets[0]);
    if (worstEff.efficiency < 50) ins.push({ type: 'action', icon: Brain, text: `${worstEff.name} scores ${worstEff.efficiency}/100 efficiency. Consider rent optimization or exit.` });

    return ins.slice(0, 5);
  }, [enrichedAssets, data]);

  // Early warnings
  const warnings = useMemo(() => {
    const w = [];
    enrichedAssets.forEach(a => {
      const m = a.metrics || {};
      if ((m.dscr || 0) < 1.2) w.push({ asset: a.name, type: 'DSCR', detail: `DSCR ${(m.dscr||0).toFixed(2)} — below 1.20`, severity: 'high' });
      if ((m.interest_rate || 0) > 4) w.push({ asset: a.name, type: 'Rate', detail: `Rate ${m.interest_rate}% — above market`, severity: 'medium' });
    });
    return w.sort((a, b) => a.severity === 'high' ? -1 : 1);
  }, [enrichedAssets]);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><p className="text-[14px] text-[#8C877F] animate-pulse">Loading...</p></div>;
  if (!data) return <div className="text-center py-20 text-[#8C877F]">No cashflow data.</div>;

  const s = data.summary;
  const stress = data.stress_test?.rent_drop_20 || {};
  // Mock deltas
  const deltaMo = s.total_monthly_cashflow * 0.03;
  const delta12m = s.total_monthly_cashflow * 0.08;
  const sparkData = Array.from({ length: 12 }, (_, i) => s.total_monthly_cashflow * (0.92 + i * 0.008 + Math.sin(i) * 0.01));
  const stressDscrPort = s.portfolio_dscr * 0.92;
  const liquidityMonths = Math.round((285000 + 150000) / s.total_monthly_debt_service);

  const toggleSort = (col) => { if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(col); setSortDir('asc'); } };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="cashflow-engine-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Cashflow Control Room</p>
          <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Cash is King</h1>
        </div>
      </div>

      {/* Hero KPIs + AI Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* KPI Grid */}
        <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-5">
          {[
            { l: 'Monthly Income', v: fmt(s.total_monthly_income), delta: deltaMo, spark: sparkData, good: true },
            { l: 'Monthly Debt Service', v: fmt(s.total_monthly_debt_service), delta: 0, spark: null },
            { l: 'Net Cashflow', v: fmt(s.total_monthly_cashflow), delta: deltaMo, spark: sparkData, good: s.total_monthly_cashflow > 0 },
            { l: 'Portfolio DSCR', v: s.portfolio_dscr.toFixed(2), delta: null, spark: null, status: { v: s.portfolio_dscr, good: 1.25, warn: 1.1 } },
            { l: 'Stress DSCR (+1%)', v: stressDscrPort.toFixed(2), delta: null, spark: null, status: { v: stressDscrPort, good: 1.25, warn: 1.1 } },
            { l: 'Liquidity Buffer', v: `${liquidityMonths} months`, delta: null, spark: null, status: { v: liquidityMonths, good: 12, warn: 6 } },
          ].map((k, i) => (
            <div key={k.l} className="bg-white border border-[#E2DDD3] rounded-xl p-4 space-y-2" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-[#8C877F] font-sans font-medium">{k.l}</p>
                {k.status && <StatusDot value={k.status.v} good={k.status.good} warn={k.status.warn} />}
              </div>
              <p className="text-[28px] font-sans font-semibold text-[#1C1C1C] leading-none tracking-[-0.01em]">{k.v}</p>
              <div className="flex items-center gap-3">
                {k.delta != null && k.delta !== 0 && (
                  <span className={`flex items-center gap-0.5 text-[11px] font-sans font-medium ${k.delta >= 0 ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>
                    {k.delta >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {fmt(Math.abs(k.delta))} vs prev. month
                  </span>
                )}
                {k.spark && <MiniSparkline data={k.spark} color={k.good ? '#2E6F57' : '#8A3C3C'} />}
              </div>
            </div>
          ))}
        </div>

        {/* AI Insights Panel */}
        <div className="bg-white border border-[#E2DDD3] rounded-xl p-5 space-y-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">AI Insights</p></div>
          <div className="space-y-3">
            {insights.map((ins, i) => {
              const Icon = ins.icon;
              const color = ins.type === 'risk' ? '#8A3C3C' : ins.type === 'stress' ? '#A67C52' : '#2E6F57';
              return (
                <div key={i} className="flex items-start gap-2.5 py-2 border-b border-[#F4F1EB] last:border-0 last:pb-0 first:pt-0">
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: color + '10' }}>
                    <Icon className="w-3 h-3" style={{ color }} />
                  </div>
                  <p className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed">{ins.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Forecast with Scenarios */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="flex items-center justify-between mb-5">
          <p className="text-[12px] text-[#8C877F] font-sans font-medium">12-Month Cashflow Forecast</p>
          <div className="flex items-center gap-1.5">
            {[
              { id: 'base', label: 'Base Case' },
              { id: 'rent', label: 'Rent +1%' },
              { id: 'vacancy', label: 'Vacancy +10%' },
              { id: 'stress', label: 'Combined Stress' },
            ].map(sc => (
              <button key={sc.id} onClick={() => setScenario(sc.id)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-sans font-medium transition-all ${scenario === sc.id ? 'bg-[#1F3A34] text-white' : 'bg-[#F4F1EB] text-[#5A5A5A] hover:bg-[#ECE7DE]'}`}>
                {sc.label}
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={forecast}>
            <defs>
              <linearGradient id="cfFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1F3A34" stopOpacity={0.08} /><stop offset="95%" stopColor="#1F3A34" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid stroke="#E3DED6" vertical={false} />
            <XAxis dataKey="month" tick={{ fill: '#8C877F', fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#8C877F', fontSize: 11, fontFamily: 'Inter, system-ui, sans-serif' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1e3).toFixed(0)}k`} />
            <Tooltip contentStyle={tip} formatter={v => [fmt(v), '']} />
            <ReferenceLine y={s.total_monthly_debt_service * 12} stroke="#8A3C3C" strokeDasharray="6 3" label={{ value: 'Break-even', position: 'right', fill: '#8A3C3C', fontSize: 10 }} />
            <Area type="monotone" dataKey="cumBase" stroke="#1F3A34" strokeWidth={1.8} fill="url(#cfFill)" name="Base" />
            {scenario === 'rent' && <Area type="monotone" dataKey="cumRent" stroke="#2E6F57" strokeWidth={1.2} fill="none" strokeDasharray="4 3" name="Rent +1%" />}
            {scenario === 'vacancy' && <Area type="monotone" dataKey="cumVac" stroke="#A67C52" strokeWidth={1.2} fill="none" strokeDasharray="4 3" name="Vacancy +10%" />}
            {scenario === 'stress' && <Area type="monotone" dataKey="cumStress" stroke="#8A3C3C" strokeWidth={1.2} fill="none" strokeDasharray="4 3" name="Stress" />}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Liquidity Bar */}
      <div className="grid grid-cols-3 gap-5">
        {[
          { l: 'Cash Runway', v: `${liquidityMonths} months`, detail: `€${(285000 + 150000).toLocaleString()} available`, good: liquidityMonths >= 12, warn: liquidityMonths >= 6 },
          { l: 'Next Rate Reset', v: 'Jun 2025', detail: 'Keizersgracht 88 — ABN AMRO', good: false, warn: true },
          { l: 'Next Loan Maturity', v: 'Dec 2025', detail: 'Vismarkt 3 — €248,000', good: false, warn: true },
        ].map((item, i) => (
          <div key={i} className="bg-white border border-[#E2DDD3] rounded-xl p-4 flex items-center gap-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className={`w-2 h-10 rounded-full ${item.good ? 'bg-[#2E6F57]' : item.warn ? 'bg-[#A67C52]' : 'bg-[#8A3C3C]'}`} />
            <div>
              <p className="text-[11px] text-[#8C877F] font-sans font-medium">{item.l}</p>
              <p className="text-[16px] font-sans font-semibold text-[#1C1C1C]">{item.v}</p>
              <p className="text-[11px] text-[#8C877F]">{item.detail}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Asset Table */}
      <div className="bg-white border border-[#E2DDD3] rounded-xl overflow-hidden" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div className="grid grid-cols-9 gap-1 px-5 py-3 border-b border-[#E2DDD3] text-[10px] text-[#8C877F] font-sans font-medium uppercase tracking-wider">
          <span className="col-span-2">Asset</span>
          <button onClick={() => toggleSort('cashflow')} className="text-right hover:text-[#1C1C1C] cursor-pointer">Net CF/mo {sortBy === 'cashflow' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('dscr')} className="text-right hover:text-[#1C1C1C] cursor-pointer">DSCR {sortBy === 'dscr' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('irr')} className="text-right hover:text-[#1C1C1C] cursor-pointer">IRR {sortBy === 'irr' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <button onClick={() => toggleSort('efficiency')} className="text-right hover:text-[#1C1C1C] cursor-pointer">Efficiency {sortBy === 'efficiency' && (sortDir === 'asc' ? '↑' : '↓')}</button>
          <span className="text-right">Refi Potential</span>
          <span className="text-right">Stress DSCR</span>
          <span className="text-center">Risk</span>
        </div>
        {sortedAssets.map(a => {
          const m = a.metrics || {};
          const lowDscr = (m.dscr || 0) < 1.2;
          return (
            <div key={a.asset_id} className={`grid grid-cols-9 gap-1 px-5 py-3.5 border-b border-[#F4F1EB] last:border-0 text-[13px] transition-colors ${lowDscr ? 'bg-[#FDF5F5]/40' : 'hover:bg-[#F4F1EB]/40'}`}>
              <div className="col-span-2">
                <p className="font-sans font-medium text-[#1C1C1C]">{a.name}</p>
                <p className="text-[11px] text-[#8C877F]">{a.city}</p>
              </div>
              <p className={`text-right font-sans font-semibold self-center ${(m.net_cashflow||0) >= 0 ? 'text-[#2E6F57]' : 'text-[#8A3C3C]'}`}>{fmt((m.net_cashflow||0)/12)}</p>
              <p className={`text-right font-sans font-semibold self-center ${(m.dscr||0) >= 1.2 ? 'text-[#1C1C1C]' : 'text-[#8A3C3C]'}`}>{(m.dscr||0).toFixed(2)}</p>
              <p className="text-right font-sans font-semibold text-[#1F3A34] self-center">{(m.irr||0).toFixed(1)}%</p>
              <div className="flex items-center justify-end gap-1.5 self-center">
                <div className="w-12 h-1.5 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full" style={{ width: `${a.efficiency}%`, backgroundColor: a.efficiency >= 60 ? '#2E6F57' : a.efficiency >= 40 ? '#A67C52' : '#8A3C3C' }} /></div>
                <span className="text-[12px] font-sans font-semibold text-[#1C1C1C] w-6 text-right">{a.efficiency}</span>
              </div>
              <p className={`text-right font-sans self-center ${a.refinancePotential > 0 ? 'font-semibold text-[#2E6F57]' : 'text-[#8C877F]'}`}>{a.refinancePotential > 0 ? fmt(a.refinancePotential) : '—'}</p>
              <p className={`text-right font-sans font-semibold self-center ${a.stressDscr >= 1.2 ? 'text-[#1C1C1C]' : 'text-[#8A3C3C]'}`}>{a.stressDscr.toFixed(2)}</p>
              <div className="flex items-center justify-center gap-1 self-center">
                {a.riskFlags.length > 0 ? a.riskFlags.map((f, i) => (
                  <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#8A3C3C]/10 text-[#8A3C3C] font-medium">{f}</span>
                )) : <span className="text-[9px] text-[#2E6F57]">Clear</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Early Warnings */}
      {warnings.length > 0 && (
        <div className="bg-white border border-[#A67C52]/20 rounded-xl p-5 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#A67C52]" /><p className="text-[13px] text-[#1C1C1C] font-sans font-semibold">Early Warnings ({warnings.length})</p></div>
          {warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-[#F4F1EB] last:border-0">
              <div className={`w-2 h-2 rounded-full ${w.severity === 'high' ? 'bg-[#8A3C3C]' : 'bg-[#A67C52]'}`} />
              <span className="text-[12px] font-sans font-medium text-[#1C1C1C] w-40">{w.asset}</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-[#F4F1EB] text-[#5A5A5A] font-medium">{w.type}</span>
              <span className="text-[12px] text-[#8C877F]">{w.detail}</span>
            </div>
          ))}
        </div>
      )}

      {/* Decision Suggestions */}
      <div className="bg-[#E6EFEA] border border-[#1F3A34]/10 rounded-xl p-5 space-y-3">
        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#1F3A34]" /><p className="text-[13px] text-[#1F3A34] font-sans font-semibold">Decision Suggestions</p></div>
        {enrichedAssets.filter(a => a.refinancePotential > 0).slice(0, 2).map((a, i) => (
          <p key={i} className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
            Refinance <strong>{a.name}</strong> at current market rate → saves <strong className="text-[#2E6F57]">{fmt(a.refinancePotential)}/yr</strong> and improves DSCR by ~{((a.refinancePotential / (a.metrics?.annual_debt_service || 1)) * a.metrics?.dscr || 0).toFixed(2)}x.
          </p>
        ))}
        {enrichedAssets.filter(a => a.efficiency < 50).slice(0, 1).map((a, i) => (
          <p key={`eff-${i}`} className="text-[12px] text-[#1C1C1C] font-sans leading-relaxed pl-6">
            <strong>{a.name}</strong> scores {a.efficiency}/100 efficiency. Rent optimization or repositioning could improve annual CF by est. <strong className="text-[#2E6F57]">{fmt((a.metrics?.annual_rent || 0) * 0.08)}</strong>.
          </p>
        ))}
      </div>
    </div>
  );
}
